<template>
  <div class="common-provider-map">
    <!-- Map Card -->
    <div v-if="internalProviders.length > 0" class="map-card">
      <div class="map-header">
        <h3 class="map-title">
          <i class="bi bi-geo-alt-fill"></i>
          Map of Provider Companies
        </h3>
      </div>
      <div class="map-container" ref="mapRef"></div>
    </div>

    <!-- Selected Provider Card (Redesigned to match list provider style) -->
    <div v-if="internalSelectedProvider" class="provider-card highlighted">
      <div class="provider-card-header">
        <div class="provider-card-title">
          <h3 class="provider-name">{{ getProviderName(internalSelectedProvider) }}</h3>
          <div class="provider-meta">
            <!-- Nur anzeigen, wenn es KEIN VCG Provider ist -->
            <span class="provider-category" v-if="getBusinessCategory(internalSelectedProvider) && !isVcgProvider(internalSelectedProvider)">
              {{ getBusinessCategory(internalSelectedProvider) }}
            </span>
            <span class="provider-distance" v-if="getDistance(internalSelectedProvider)">
              <i class="bi bi-geo-alt"></i> {{ getDistance(internalSelectedProvider) }} km
            </span>
            <span class="map-selection-badge">
              <i class="bi bi-pin-map-fill"></i> Selected on map
            </span>
          </div>
        </div>
        
        <button class="close-btn" @click="clearSelectedProvider" aria-label="Close provider details">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      
      <div class="provider-card-content">
        <!-- Provider Details -->
        <div class="provider-details">
          <!-- Description -->
          <div class="detail-section" v-if="hasDescription(internalSelectedProvider)">
            <div class="section-header">
              <i class="bi bi-info-circle"></i>
              <h4 class="section-title">Description</h4>
            </div>
            <p class="description-text">{{ getDescription(internalSelectedProvider) }}</p>
          </div>
          
          <!-- Institution Types - NEW -->
          <div class="detail-section" v-if="hasInstitutionTypes(internalSelectedProvider)">
            <div class="section-header">
              <i class="bi bi-building"></i>
              <h4 class="section-title">Institution Types</h4>
            </div>
            <div class="tags-wrapper">
              <span
                v-for="(type, idx) in internalSelectedProvider.institution_types"
                :key="`type-${idx}`"
                class="institution-tag"
              >
                {{ type }}
              </span>
            </div>
          </div>
          
          <!-- Capacity - NEW -->
          <div class="detail-section" v-if="hasCapacity(internalSelectedProvider)">
            <div class="section-header">
              <i class="bi bi-box-seam"></i>
              <h4 class="section-title">Food Capacity</h4>
              <div class="section-hint">Types of food this provider needs and their capacity</div>
            </div>
            <div class="capacity-grid">
              <div 
                v-for="(item, idx) in internalSelectedProvider.capacity" 
                :key="`capacity-${idx}`"
                class="capacity-item"
              >
                <div class="capacity-category">{{ item.food_category }}</div>
                <div class="capacity-details">
                  <div class="capacity-status">
                    <span class="status-badge" :class="getNeededClass(item.needed)">
                      {{ item.needed }}
                    </span>
                  </div>
                  <div class="capacity-info" v-if="item.max_kg_per_week">
                    <span class="capacity-label">Maximum:</span>
                    <span class="capacity-value">{{ item.max_kg_per_week }} kg/week</span>
                  </div>
                  <div class="capacity-info" v-if="item.note">
                    <span class="capacity-label">Note:</span>
                    <span class="capacity-value">{{ item.note }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Logistics - NEW -->
          <div class="detail-section" v-if="hasLogistics(internalSelectedProvider)">
            <div class="section-header">
              <i class="bi bi-truck"></i>
              <h4 class="section-title">Logistics</h4>
              <div class="section-hint">Pickup and delivery options</div>
            </div>
            <div class="logistics-container">
              <!-- Pickup Section -->
              <div class="logistics-section" v-if="internalSelectedProvider.logistics.pickup">
                <h5 class="logistics-title">
                  <i class="bi bi-box-arrow-up"></i>
                  Pickup
                </h5>
                <div class="logistics-details">
                  <div class="logistics-detail-item">
                    <span class="logistics-label">Available:</span>
                    <span class="logistics-value">{{ internalSelectedProvider.logistics.pickup.available ? 'Yes' : 'No' }}</span>
                  </div>
                  <div class="logistics-detail-item" v-if="internalSelectedProvider.logistics.pickup.radius_km">
                    <span class="logistics-label">Radius:</span>
                    <span class="logistics-value">{{ internalSelectedProvider.logistics.pickup.radius_km }} km</span>
                  </div>
                  <div class="logistics-detail-item" v-if="internalSelectedProvider.logistics.pickup.frequency">
                    <span class="logistics-label">Frequency:</span>
                    <span class="logistics-value">{{ internalSelectedProvider.logistics.pickup.frequency }}</span>
                  </div>
                  <div class="logistics-detail-item">
                    <span class="logistics-label">Weekend Pickup:</span>
                    <span class="logistics-value">{{ internalSelectedProvider.logistics.pickup.weekend ? 'Yes' : 'No' }}</span>
                  </div>
                </div>
              </div>
              
              <!-- Delivery Section -->
              <div class="logistics-section" v-if="internalSelectedProvider.logistics.delivery">
                <h5 class="logistics-title">
                  <i class="bi bi-box-arrow-in-down"></i>
                  Delivery
                </h5>
                <div class="logistics-details">
                  <div class="logistics-detail-item">
                    <span class="logistics-label">Accepted:</span>
                    <span class="logistics-value">{{ internalSelectedProvider.logistics.delivery.accepted ? 'Yes' : 'No' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Company Constraints -->
          <div class="detail-section" v-if="hasCompanyConstraints(internalSelectedProvider)">
            <div class="section-header">
              <i class="bi bi-shield-check"></i>
              <h4 class="section-title">Operational Requirements</h4>
              <div class="section-hint">
                Any rules or conditions the provider requires for handling surplus
              </div>
            </div>
            <div class="constraints-grid">
              <div class="constraint-item" v-if="getPreparation(internalSelectedProvider)">
                <div class="constraint-label">Preparation</div>
                <div class="constraint-value">{{ getPreparation(internalSelectedProvider) }}</div>
              </div>
              
              <div class="constraint-item" v-if="getMinimumQuantity(internalSelectedProvider)">
                <div class="constraint-label">Minimum Quantity</div>
                <div class="constraint-value">{{ getMinimumQuantity(internalSelectedProvider) }}</div>
              </div>
              
              <div class="constraint-item" v-if="getFrequency(internalSelectedProvider)">
                <div class="constraint-label">Frequency</div>
                <div class="constraint-value">{{ getFrequency(internalSelectedProvider) }}</div>
              </div>
              
              <div class="constraint-item" v-if="getAdditionalRequirements(internalSelectedProvider)">
                <div class="constraint-label">Additional Requirements</div>
                <div class="constraint-value">{{ getAdditionalRequirements(internalSelectedProvider) }}</div>
              </div>
            </div>
          </div>
          
          <!-- Accepted Food Types -->
          <div class="detail-section" v-if="hasAcceptedFoodTypes(internalSelectedProvider)">
            <div class="section-header">
              <i class="bi bi-basket"></i>
              <h4 class="section-title">Accepted Food Types</h4>
              <div class="section-hint">
                Food categories and items this provider can work with
              </div>
            </div>
            <div class="food-types-grid">
              <div class="food-type-category" v-if="hasFoodGroups(internalSelectedProvider)">
                <div class="category-label">Food Groups</div>
                <div class="tags-wrapper">
                  <span 
                    v-for="(group, idx) in internalSelectedProvider.accepted_food_types.food_group" 
                    :key="`group-${idx}`"
                    class="food-tag"
                  >
                    {{ group }}
                  </span>
                </div>
              </div>
              
              <div class="food-type-category" v-if="hasFoodSubgroups(internalSelectedProvider)">
                <div class="category-label">Food Subgroups</div>
                <div class="tags-wrapper">
                  <span 
                    v-for="(subgroup, idx) in internalSelectedProvider.accepted_food_types.food_subgroup" 
                    :key="`subgroup-${idx}`"
                    class="food-tag"
                  >
                    {{ subgroup }}
                  </span>
                </div>
              </div>
              
              <div class="food-type-category" v-if="hasSurplusNames(internalSelectedProvider)">
                <div class="category-label">Specific Surplus Items</div>
                <div class="tags-wrapper">
                  <span 
                    v-for="(name, idx) in internalSelectedProvider.accepted_food_types.surplus_name" 
                    :key="`surplus-${idx}`"
                    class="food-tag"
                  >
                    {{ name }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Business Categories - Only for VCG providers -->
          <div class="detail-section" v-if="hasBusinessCategories(internalSelectedProvider)">
            <div class="section-header">
              <i class="bi bi-briefcase"></i>
              <h4 class="section-title">Business Categories</h4>
              <div class="section-hint">Sectors in which the provider operates</div>
            </div>
            <div class="business-categories">
              <span 
                v-for="(category, cIdx) in internalSelectedProvider.businessCategory" 
                :key="`category-${cIdx}`"
                class="business-category-tag"
              >
                {{ category }}
              </span>
            </div>
          </div>
          
          <!-- Additional Company Information -->
          <div class="detail-section" v-if="hasAdditionalInfo(internalSelectedProvider)">
            <div class="section-header">
              <i class="bi bi-plus-circle"></i>
              <h4 class="section-title">Additional Information</h4>
              <div class="section-hint">
                Additional details provided by the company
              </div>
            </div>
            <div class="additional-info">
              <div 
                v-for="(value, key) in filteredAdditionalInfo(internalSelectedProvider)" 
                :key="key"
                class="additional-item"
              >
                <div class="additional-label">{{ formatKey(key) }}</div>
                <div class="additional-value" v-if="key === 'website'">
                  <a :href="formatUrl(value)" target="_blank" rel="noopener">{{ value }}</a>
                </div>
                <div class="additional-value" v-else>
                  {{ value }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Contact Information -->
          <div class="detail-section">
            <div class="section-header">
              <i class="bi bi-person-badge"></i>
              <h4 class="section-title">Contact Information</h4>
            </div>
            <div class="contact-info">
              <div class="contact-item" v-if="getContactPerson(internalSelectedProvider)">
                <div class="contact-label">Contact Person</div>
                <div class="contact-value">{{ getContactPerson(internalSelectedProvider) }}</div>
              </div>
              
              <div class="contact-item" v-if="getEmail(internalSelectedProvider)">
                <div class="contact-label">Email</div>
                <div class="contact-value">
                  <a :href="`mailto:${getEmail(internalSelectedProvider)}`">{{ getEmail(internalSelectedProvider) }}</a>
                </div>
              </div>
              
              <div class="contact-item" v-if="getPhone(internalSelectedProvider)">
                <div class="contact-label">Phone</div>
                <div class="contact-value">{{ getPhone(internalSelectedProvider) }}</div>
              </div>
              
              <div class="contact-item">
                <div class="contact-label">Location</div>
                <div class="contact-value">{{ formatAddress(getLocation(internalSelectedProvider)) || 'Not specified' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default icon paths for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default {
  name: 'CommonProviderMap',
  props: {
    providers: {
      type: Array,
      default: () => []
    },
    userLat: {
      type: Number,
      default: null
    },
    userLon: {
      type: Number,
      default: null
    },
    mapTitle: {
      type: String,
      default: 'Map of Provider Companies'
    },
    selectedProvider: {
      type: Object,
      default: null
    }
  },
  emits: ['provider-selected'], // Add provider-selected event
  setup(props, { emit }) {
    const mapRef = ref(null);
    let mapInstance = null;
    let markersLayer = null;
    let markers = {};

    const internalProviders = ref([]);
    const internalSelectedProvider = ref(null);

    // Watch for changes in the selectedProvider prop
    watch(() => props.selectedProvider, (newProvider) => {
      if (newProvider) {
        internalSelectedProvider.value = newProvider;
        highlightSelectedMarker(newProvider);
      }
    }, { immediate: true });

    /**
     * Function to update the selected provider from external components
     */
    function updateSelectedProvider(provider) {
      internalSelectedProvider.value = provider;
      highlightSelectedMarker(provider);
    }

    function clearSelectedProvider() {
      internalSelectedProvider.value = null;
      if (markersLayer) {
        markersLayer.eachLayer((layer) => {
          if (layer.isPopupOpen()) {
            layer.closePopup();
          }
        });
      }
    }

    function highlightSelectedMarker(provider) {
      if (!provider || !mapInstance) return;

      // Find provider ID for highlighting
      const providerId = getProviderId(provider);
      
      if (providerId && markers[providerId]) {
        // Open popup for this marker
        markers[providerId].openPopup();
        
        // Center the map on this marker
        const coords = getProviderCoordinates(provider);
        if (coords) {
          mapInstance.setView([coords[1], coords[0]], 11);
        }
      }
    }

    // Format URL to ensure it has http(s)
    function formatUrl(url) {
      if (!url) return '#';
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      return `https://${url}`;
    }

    // Get a provider ID for tracking markers
    function getProviderId(provider) {
      if (!provider) return null;
      
      // For Foodcycle providers
      if (provider.company_name) {
        return provider.company_name;
      }
      
      // For VCG providers
      if (provider.companyName) {
        return provider.companyName;
      }
      
      // Fallback to _id
      return provider._id || null;
    }

    // Check if provider is from VCG
    function isVcgProvider(provider) {
      if (!provider) return false;
      
      // Check for VCG specific properties
      return !!provider.companyName || provider.provider_origin === 'vcg.ai';
    }

    // Get provider name from the provider object (unified)
    function getProviderName(provider) {
      if (!provider) return 'Unknown Provider';
      
      // For Foodcycle providers
      if (provider.company_name) {
        return provider.company_name;
      }
      
      // For VCG providers
      if (provider.companyName) {
        return provider.companyName;
      }
      
      return 'Unnamed Provider';
    }
    
    // Unified helper functions to get provider data regardless of source
    function getDescription(provider) {
      return provider.description || '';
    }
    
    function hasDescription(provider) {
      return !!getDescription(provider);
    }
    
    function getBusinessCategory(provider) {
      // For Foodcycle
      if (provider.business_category) {
        return provider.business_category;
      }
      // For VCG - could return the first category if it exists
      if (Array.isArray(provider.businessCategory) && provider.businessCategory.length > 0) {
        return provider.businessCategory.join(', ');
      }
      return '';
    }
    
    function getLocation(provider) {
      return provider.location || null;
    }
    
    function getDistance(provider) {
      if (provider.dist && typeof provider.dist.calculated === 'number') {
        return (provider.dist.calculated / 1000).toFixed(2);
      }
      if (provider.dist_km) {
        return provider.dist_km.toFixed(2);
      }
      return null;
    }
    
    function getContactPerson(provider) {
      return provider.contact_person || '';
    }
    
    function getEmail(provider) {
      if (provider.contact && provider.contact.email) {
        return provider.contact.email;
      }
      return '';
    }
    
    function getPhone(provider) {
      if (provider.contact && provider.contact.phone) {
        return provider.contact.phone;
      }
      return '';
    }
    
    function hasContactInfo(provider) {
      return getContactPerson(provider) || getEmail(provider) || getPhone(provider);
    }
    
    // Constraint helpers
    function getPreparation(provider) {
      return provider.company_constraints?.preparation || '';
    }
    
    function getMinimumQuantity(provider) {
      return provider.company_constraints?.minimum_quantity_acceptance || '';
    }
    
    function getFrequency(provider) {
      return provider.company_constraints?.frequency || '';
    }
    
    function getAdditionalRequirements(provider) {
      return provider.company_constraints?.additional_requirements || '';
    }

    // NEW Helper for additional_company_information/infomation (typo in DB)
    function getAdditionalInfo(provider) {
      // Check both variants due to typo in the database
      if (provider.additional_company_information) {
        return provider.additional_company_information;
      }
      if (provider.additional_company_infomation) {
        return provider.additional_company_infomation;
      }
      return {};
    }
    
    // Filter additional info to exclude certain fields
    function filteredAdditionalInfo(provider) {
      const info = getAdditionalInfo(provider);
      const result = {};
      
      // Copy all properties except the excluded ones
      for (const key in info) {
        if (!['non_profit', 'umbrella_organization', 'opened_year'].includes(key)) {
          result[key] = info[key];
        }
      }
      
      return result;
    }

    // Get provider coordinates
    function getProviderCoordinates(provider) {
      if (provider.location_geo && provider.location_geo.type === 'Point' && 
          Array.isArray(provider.location_geo.coordinates)) {
        return provider.location_geo.coordinates; // [lng, lat]
      }
      else if (provider.location && provider.location.latitude && provider.location.longitude) {
        // VCG
        return [provider.location.longitude, provider.location.latitude];
      }
      return null;
    }

    // Format address from provider location object
    const formatAddress = (loc) => {
      if (!loc) return '';
      const addr = loc.address || '';
      const city = loc.city || '';
      const postal = loc.postalCode || loc.postal_code || '';
      const country = loc.country || '';
      let combined = `${addr}, ${postal} ${city}, ${country}`.replace(/\s+,/g, '');
      combined = combined.trim().replace(/\s+/g, ' ');
      return combined || '';
    };

    // Format key für additional information
    const formatKey = (key) => {
      return key
        // Insert space before capital letters
        .replace(/([A-Z])/g, ' $1')
        // Replace underscores with spaces
        .replace(/_/g, ' ')
        // Capitalize first letter
        .replace(/^./, str => str.toUpperCase())
        // Capitalize after spaces
        .replace(/\s(.)/g, (match, group) => ` ${group.toUpperCase()}`);
    };

    // NEW helpers for the new fields
    const hasCapacity = (provider) => {
      return provider && Array.isArray(provider.capacity) && provider.capacity.length > 0;
    };

    // Keep these for compatibility but we won't display them
    const hasChallenges = (provider) => {
      return provider && Array.isArray(provider.challenges) && provider.challenges.length > 0;
    };

    const hasDigitalPlatforms = (provider) => {
      return provider && Array.isArray(provider.digital_platforms_used);
    };

    const hasDisposalMethods = (provider) => {
      return provider && Array.isArray(provider.disposal_methods) && provider.disposal_methods.length > 0;
    };

    const hasInstitutionTypes = (provider) => {
      return provider && Array.isArray(provider.institution_types) && provider.institution_types.length > 0;
    };

    const hasLogistics = (provider) => {
      return provider && provider.logistics && 
        (provider.logistics.pickup || provider.logistics.delivery);
    };

    // Helper for capacity statuses
    const getNeededClass = (needed) => {
      if (needed === 'always') return 'needed-always';
      if (needed === 'sometimes') return 'needed-sometimes';
      if (needed === 'rarely') return 'needed-rarely';
      return '';
    };

    // Check helper functions for conditional rendering
    const hasCompanyConstraints = (provider) => {
      return provider && provider.company_constraints && (
        provider.company_constraints.preparation ||
        provider.company_constraints.minimum_quantity_acceptance ||
        provider.company_constraints.frequency ||
        provider.company_constraints.additional_requirements
      );
    };

    const hasAcceptedFoodTypes = (provider) => {
      return provider && provider.accepted_food_types && (
        hasFoodGroups(provider) ||
        hasFoodSubgroups(provider) ||
        hasSurplusNames(provider)
      );
    };

    const hasFoodGroups = (provider) => {
      return provider && 
             provider.accepted_food_types && 
             Array.isArray(provider.accepted_food_types.food_group) && 
             provider.accepted_food_types.food_group.length > 0;
    };

    const hasFoodSubgroups = (provider) => {
      return provider && 
             provider.accepted_food_types && 
             Array.isArray(provider.accepted_food_types.food_subgroup) && 
             provider.accepted_food_types.food_subgroup.length > 0;
    };

    const hasSurplusNames = (provider) => {
      return provider && 
             provider.accepted_food_types && 
             Array.isArray(provider.accepted_food_types.surplus_name) && 
             provider.accepted_food_types.surplus_name.length > 0;
    };

    const hasBusinessCategories = (provider) => {
      return provider && 
             Array.isArray(provider.businessCategory) && 
             provider.businessCategory.length > 0;
    };

    // Modified to check both variants of additional_company_info field and filter out excluded fields
    const hasAdditionalInfo = (provider) => {
      const filteredInfo = filteredAdditionalInfo(provider);
      return filteredInfo && Object.keys(filteredInfo).length > 0;
    };

    // Watch for changes in providers
    watch(
      () => props.providers,
      (newVal) => {
        internalProviders.value = newVal || [];
        if (mapInstance) {
          addMarkersToMap(internalProviders.value);
        }
      },
      { immediate: true }
    );

    function initMap() {
      if (!mapRef.value) return;
      mapInstance = L.map(mapRef.value).setView([48.2082, 16.3738], 7);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(mapInstance);
      markersLayer = L.layerGroup().addTo(mapInstance);
      addMarkersToMap(internalProviders.value);

      // If we already have a selected provider, highlight it
      if (props.selectedProvider) {
        internalSelectedProvider.value = props.selectedProvider;
        highlightSelectedMarker(props.selectedProvider);
      }
    }

    function addMarkersToMap(providerList) {
      if (!mapInstance || !markersLayer) return;
      markersLayer.clearLayers();
      markers = {};
      const latLngArray = [];

      // 1) Provider markers (blue)
      providerList.forEach((prov) => {
        const coords = getProviderCoordinates(prov);
        if (coords) {
          const [lng, lat] = coords;
          // Standard Leaflet marker (blue)
          const marker = L.marker([lat, lng]);
          const providerName = getProviderName(prov);
          marker.bindPopup(`<strong>${providerName}</strong>`);
          marker.on('click', () => {
            internalSelectedProvider.value = prov;
            emit('provider-selected', prov); // Emit event when a provider is selected on the map
          });
          marker.addTo(markersLayer);
          
          // Store marker reference by provider ID
          const providerId = getProviderId(prov);
          if (providerId) {
            markers[providerId] = marker;
          }
          
          latLngArray.push([lat, lng]);
        }
      });

      // 2) User location marker (red)
      if (props.userLat !== null && props.userLon !== null) {
        const redIcon = L.icon({
          iconUrl: 'https://cdn-icons-png.flaticon.com/512/252/252025.png',
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32]
        });

        const userMarker = L.marker([props.userLat, props.userLon], { icon: redIcon })
          .bindPopup('<strong>Your Surplus Location</strong>');

        userMarker.addTo(markersLayer);
        // Raise z-index so red marker is always on top
        userMarker.setZIndexOffset(1000);

        latLngArray.push([props.userLat, props.userLon]);
      }

      // Fit bounds to show all markers
      if (latLngArray.length > 0) {
        const bounds = L.latLngBounds(latLngArray);
        mapInstance.fitBounds(bounds, { padding: [20, 20] });
      }
      
      // Highlight the selected provider if any
      if (internalSelectedProvider.value) {
        highlightSelectedMarker(internalSelectedProvider.value);
      }
    }

    onMounted(() => {
      if (internalProviders.value.length > 0) {
        initMap();
      }
    });

    return {
      mapRef,
      internalProviders,
      internalSelectedProvider,
      formatAddress,
      hasCompanyConstraints,
      hasAcceptedFoodTypes,
      hasFoodGroups,
      hasFoodSubgroups,
      hasSurplusNames,
      hasBusinessCategories,
      hasContactInfo,
      hasAdditionalInfo,
      formatKey,
      formatUrl,
      updateSelectedProvider,
      highlightSelectedMarker,
      getProviderId,
      getProviderName,
      getProviderCoordinates,
      clearSelectedProvider,
      isVcgProvider,
      filteredAdditionalInfo,
      
      // Unified getters
      getDescription,
      hasDescription,
      getBusinessCategory,
      getLocation,
      getDistance,
      getContactPerson,
      getEmail,
      getPhone,
      getPreparation,
      getMinimumQuantity,
      getFrequency,
      getAdditionalRequirements,
      getAdditionalInfo,

      // NEW helpers for new fields
      hasCapacity,
      hasChallenges, 
      hasDigitalPlatforms,
      hasDisposalMethods,
      hasInstitutionTypes,
      hasLogistics,
      getNeededClass
    };
  }
};
</script>

<style scoped>
.common-provider-map {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Map Card Styling */
.map-card {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #eee;
}

.map-header {
  background-color: #f2f5e7;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.map-title {
  margin: 0;
  color: #333;
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.map-title i {
  color: #87b140;
}

.map-container {
  width: 100%;
  height: 450px;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

/* Provider Card Styling - Matched with list style */
.provider-card {
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  transition: all 0.3s ease;
}

.provider-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.provider-card.highlighted {
  border: 2px solid #87b140;
  box-shadow: 0 0 0 3px rgba(135, 177, 64, 0.2), 0 8px 25px rgba(0, 0, 0, 0.1);
}

.provider-card-header {
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f2f5e7;
  border-bottom: 1px solid rgba(135, 177, 64, 0.2);
}

.provider-card-title {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.provider-name {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
}

.provider-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.provider-category {
  font-size: 0.9rem;
  color: #666;
}

.provider-distance {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.9rem;
  color: #666;
}

.provider-distance i {
  color: #87b140;
}

.map-selection-badge {
  background-color: rgba(135, 177, 64, 0.15);
  color: #558000;
  padding: 0.3rem 0.7rem;
  border-radius: 30px;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.close-btn {
  background-color: rgba(0, 0, 0, 0.05);
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #e74c3c;
}

.provider-card-content {
  padding: 0;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.provider-details {
  display: flex;
  flex-direction: column;
}

.detail-section {
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.detail-section:last-child {
  border-bottom: none;
}

.section-header {
  margin-bottom: 1.25rem;
}

.section-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-header i {
  color: #87b140;
  font-size: 1.1rem;
}

.section-hint {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
}

.description-text {
  color: #555;
  line-height: 1.6;
  margin: 0;
}

/* Tags and badges general styles */
.tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.institution-tag, .platform-tag {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  border: 1px solid rgba(135, 177, 64, 0.15);
}

/* NEW: Capacity styles */
.capacity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.capacity-item {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eee;
}

.capacity-category {
  padding: 0.75rem 1rem;
  background-color: #f2f5e7;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
}

.capacity-details {
  padding: 1rem;
}

.capacity-status {
  margin-bottom: 0.75rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
}

.needed-always {
  background-color: rgba(76, 175, 80, 0.15);
  color: #2e7d32;
}

.needed-sometimes {
  background-color: rgba(255, 193, 7, 0.15);
  color: #f57f17;
}

.needed-rarely {
  background-color: rgba(244, 67, 54, 0.15);
  color: #c62828;
}

.capacity-info {
  display: flex;
  margin-bottom: 0.5rem;
}

.capacity-info:last-child {
  margin-bottom: 0;
}

.capacity-label {
  font-weight: 600;
  color: #555;
  width: 80px;
  flex-shrink: 0;
}

.capacity-value {
  color: #333;
}

/* NEW: Logistics styles */
.logistics-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.25rem;
}

.logistics-section {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eee;
}

.logistics-title {
  padding: 0.75rem 1rem;
  background-color: #f2f5e7;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.logistics-title i {
  color: #87b140;
}

.logistics-details {
  padding: 1rem;
}

.logistics-detail-item {
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
}

.logistics-detail-item:last-child {
  margin-bottom: 0;
}

.logistics-label {
  font-weight: 600;
  color: #555;
}

.logistics-value {
  color: #333;
}

/* Constraints Grid */
.constraints-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.constraint-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.constraint-label {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
}

.constraint-value {
  background-color: #f9f9f9;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  color: #444;
  font-size: 0.95rem;
  line-height: 1.5;
  border-left: 3px solid #87b140;
}

/* Food Types Grid */
.food-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}

.food-type-category {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.category-label {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
}

.food-tag {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  border: 1px solid rgba(135, 177, 64, 0.2);
}

/* Business Categories */
.business-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.business-category-tag {
  background-color: rgba(135, 177, 64, 0.1);
  color: #558000;
  padding: 0.4rem 0.85rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Additional Info Styles */
.additional-info {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.additional-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.additional-label {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
}

.additional-value {
  color: #333;
  font-size: 0.95rem;
}

.additional-value a {
  color: #558000;
  text-decoration: none;
  transition: color 0.2s ease;
}

.additional-value a:hover {
  color: #87b140;
  text-decoration: underline;
}

.empty-info {
  color: #888;
  font-style: italic;
  margin: 0;
}

/* Contact Info */
.contact-info {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}

.contact-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-label {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
}

.contact-value {
  color: #333;
  font-size: 0.95rem;
}

.contact-value a {
  color: #558000;
  text-decoration: none;
  transition: color 0.2s ease;
}

.contact-value a:hover {
  color: #87b140;
  text-decoration: underline;
}

/* Responsive Styles */
@media (max-width: 992px) {
  .map-container {
    height: 400px;
  }
}

@media (max-width: 768px) {
  .constraints-grid,
  .food-types-grid,
  .contact-info,
  .additional-info,
  .capacity-grid,
  .logistics-container {
    grid-template-columns: 1fr;
  }
  
  .map-container {
    height: 350px;
  }
  
  .detail-section {
    padding: 1.25rem;
  }
  
  .provider-card-header {
    padding: 1rem 1.25rem;
  }
  
  .provider-name {
    font-size: 1.1rem;
  }
}

@media (max-width: 576px) {
  .map-container {
    height: 300px;
  }
  
  .map-header {
    padding: 0.85rem 1.25rem;
  }
  
  .map-title {
    font-size: 1.1rem;
  }
}
</style>